import React, { useEffect, useRef } from "react";

import {
  Button,
  FormLayout,
  Stack,
  TextField,
  TextStyle,
} from "@shopify/polaris";

import {
  CaretUpMinor,
  CaretDownMinor,
  DeleteMinor,
} from "@shopify/polaris-icons";

import { isValidWeightDescriptor } from "util/weightValidation";

const DEFAULT_DESCRIPTION_MAX_LENGTH = 100;

/**
 * Standalone descriptor-tier editor used by every weight-override flow
 * (per-PLU modal, sub-department section, category section).
 *
 * Controlled component — descriptors and onChange are owned by the caller.
 * Internally we allocate stable row ids via a useRef counter so React keys
 * survive reordering even when callers pass descriptor objects without ids.
 *
 * Props
 * -----
 * @param {Array}    descriptors            - `[{ id?, weight, description }]`.
 * @param {function} onChange               - Receives the next descriptors array.
 * @param {number}   [maxDescriptionLength] - Cap for description input, default 100.
 */
export default function WeightDescriptorEditor({
  descriptors,
  onChange,
  maxDescriptionLength = DEFAULT_DESCRIPTION_MAX_LENGTH,
}) {
  // Stable per-row id allocator.  Rows that already carry an `id` keep it;
  // rows added by the caller without one get a fresh number on first render.
  const nextIdRef = useRef(0);
  const ensuredIdsRef = useRef(new WeakMap());

  const rows = (descriptors || []).map((descriptor) => {
    if (descriptor && descriptor.id != null) {
      return descriptor;
    }
    // Memoize the assigned id per object reference so repeated renders with
    // the same descriptor object keep the same key.
    let id = ensuredIdsRef.current.get(descriptor);
    if (id == null) {
      id = nextIdRef.current++;
      ensuredIdsRef.current.set(descriptor, id);
    }
    return { ...descriptor, id };
  });

  // Keep the id allocator ahead of any caller-provided ids so subsequent
  // internally-allocated ids don't collide with them.
  useEffect(() => {
    rows.forEach((r) => {
      if (typeof r.id === "number" && r.id >= nextIdRef.current) {
        nextIdRef.current = r.id + 1;
      }
    });
  });

  const emit = (nextRows) => {
    onChange(nextRows);
  };

  const handleWeightChange = (index, value) => {
    emit(rows.map((d, i) => (i === index ? { ...d, weight: value } : d)));
  };

  const handleDescriptionChange = (index, value) => {
    if (value.length > maxDescriptionLength) return;
    emit(rows.map((d, i) => (i === index ? { ...d, description: value } : d)));
  };

  const handleAddRow = () => {
    emit([
      ...rows,
      { id: nextIdRef.current++, weight: "", description: "" },
    ]);
  };

  const handleRemoveRow = (index) => {
    emit(rows.filter((_, i) => i !== index));
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const next = [...rows];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    emit(next);
  };

  const handleMoveDown = (index) => {
    if (index >= rows.length - 1) return;
    const next = [...rows];
    [next[index], next[index + 1]] = [next[index + 1], next[index]];
    emit(next);
  };

  // A row is invalid if its weight is empty or doesn't match the descriptor
  // grammar.  The errors render eagerly — matches existing modal behavior.
  const rowErrors = rows.map((d) => {
    const w = String(d.weight || "").trim();
    if (w === "") return "Weight is required";
    if (!isValidWeightDescriptor(w))
      return "Use a format like 300g, 1kg, or 1lb";
    return null;
  });

  return (
    <Stack vertical spacing="loose">
      {rows.length === 0 && (
        <TextStyle variation="subdued">
          No weight tiers added. Click "Add weight tier" below.
        </TextStyle>
      )}

      {rows.map((descriptor, index) => (
        <Stack
          key={descriptor.id}
          alignment="leading"
          spacing="tight"
          wrap={false}
        >
          <Stack.Item fill>
            <FormLayout>
              <FormLayout.Group>
                <TextField
                  label="Weight"
                  placeholder="e.g. 300g"
                  value={descriptor.weight}
                  onChange={(val) => handleWeightChange(index, val)}
                  autoComplete="off"
                  error={rowErrors[index]}
                />
                <TextField
                  label={`Description (${String(
                    descriptor.description || "",
                  ).length}/${maxDescriptionLength})`}
                  placeholder="e.g. ¼ inch thick"
                  value={descriptor.description}
                  onChange={(val) => handleDescriptionChange(index, val)}
                  autoComplete="off"
                  maxLength={maxDescriptionLength}
                  showCharacterCount
                />
              </FormLayout.Group>
            </FormLayout>
          </Stack.Item>

          <Stack.Item>
            <div style={{ paddingTop: 22 }}>
              <Stack spacing="extraTight">
                <Button
                  plain
                  icon={CaretUpMinor}
                  disabled={index === 0}
                  onClick={() => handleMoveUp(index)}
                  accessibilityLabel={`Move row ${index + 1} up`}
                />
                <Button
                  plain
                  icon={CaretDownMinor}
                  disabled={index === rows.length - 1}
                  onClick={() => handleMoveDown(index)}
                  accessibilityLabel={`Move row ${index + 1} down`}
                />
                <Button
                  plain
                  destructive
                  icon={DeleteMinor}
                  onClick={() => handleRemoveRow(index)}
                  accessibilityLabel={`Remove row ${index + 1}`}
                />
              </Stack>
            </div>
          </Stack.Item>
        </Stack>
      ))}

      <Button onClick={handleAddRow}>Add weight tier</Button>
    </Stack>
  );
}

/**
 * Validation helper exposed alongside the editor so callers can disable Save
 * without duplicating the rules.
 */
export function descriptorsAreValid(descriptors) {
  if (!Array.isArray(descriptors) || descriptors.length === 0) return false;
  return descriptors.every((d) =>
    isValidWeightDescriptor(String((d && d.weight) || "").trim()),
  );
}
